/**
 * L'espace, vu par l'entreprise.
 *
 * Un seul appel rend tout ce que la coque affiche : le contact, sa fiche
 * d'entreprise, les écoles avec qui il travaille, ses demandes et les profils
 * qu'on lui a envoyés. Quatre écrans, une lecture — l'espace tient dans une page
 * de données, et le découper en quatre appels ferait quatre écrans qui chargent.
 */

export const DECISIONS = ["oui", "peut-etre", "pas"] as const;
export type Decision = (typeof DECISIONS)[number];

/** Ce que l'entreprise voit d'un profil. L'école choisit ce qu'elle y met. */
export type EspaceProfile = {
	id: string;
	fullName: string;
	headline: string | null;
	program: string | null;
	location: string | null;
	availability: string | null;
	cvUrl: string | null;
	decision: Decision | null;
	decidedAt: string | null;
	comment: string | null;
	viewedAt: string | null;
};

/** Ce que l'école a envoyé en une fois, pour une offre. */
export type EspaceSelection = {
	id: string;
	brandSlug: string;
	schoolName: string;
	title: string;
	message: string | null;
	offerRequestId: string | null;
	offerJob: string | null;
	createdAt: string;
	closedAt: string | null;
	profiles: EspaceProfile[];
};

export type EspaceRequest = {
	id: string;
	brandSlug: string;
	schoolName: string;
	job: string;
	description: string | null;
	location: string | null;
	contract: string | null;
	telework: string | null;
	qualification: string | null;
	startsOn: string | null;
	headcount: number | null;
	/**
	 * Le statut EFFECTIF, pas la colonne.
	 *
	 * Une offre publiée dont la date limite est passée vaut `archivee`, sans qu'un
	 * travail de fond ait eu à réécrire quoi que ce soit. Un statut recopié dans une
	 * colonne se désynchronise le jour où personne ne fait tourner le batch.
	 */
	status: string;
	schoolNote: string | null;
	createdAt: string;
	/** La date limite posée par l'entreprise. Passée, l'offre s'archive. */
	expiresOn: string | null;
	/** Quand l'école a tranché. Les deux bornes du délai de traitement. */
	decidedAt: string | null;
	publishedAt: string | null;
	/** Les formations visées. Vide = toutes les promotions de l'école. */
	targetPrograms: string[];
	/** L'offre dont celle-ci est un redépôt, s'il y en a une. */
	duplicatedFrom: string | null;
};

export const OFFER_STATUSES = ["nouveau", "a_completer", "validee", "refusee", "archivee"] as const;

/**
 * Le statut qu'on MONTRE, dérivé de la date limite.
 *
 * Une offre publiée dont la date est passée est archivée, point. Le calcul vit ici
 * pour que le serveur et l'écran ne puissent pas en donner deux versions.
 */
export function statutEffectif(
	status: string,
	expiresOn: string | null,
	aujourdhui = new Date(),
): string {
	if (status !== "validee" || !expiresOn) return status;
	// Comparaison sur la DATE seule : une offre qui expire aujourd'hui court encore
	// jusqu'à ce soir, et l'archiver à 00h01 couperait une journée de candidatures.
	return expiresOn < jourCalendaire(aujourdhui) ? "archivee" : status;
}

/**
 * Le jour tel que l'entreprise le vit, pas tel que le serveur le compte.
 *
 * ⚠️ `toISOString()` rend le jour UTC. Le serveur tourne en UTC, l'entreprise est à
 * Paris : entre minuit et 2 h du matin en été, les deux ne sont pas le même jour, et
 * une offre serait archivée avec une à deux heures de retard sur l'intention. C'est
 * exactement la journée qu'on cherchait à ne pas couper.
 *
 * `fr-CA` est le raccourci honnête pour obtenir « AAAA-MM-JJ » : c'est le seul
 * format de date que cette locale produit, et il se compare comme une chaîne.
 */
export function jourCalendaire(quand = new Date(), fuseau = "Europe/Paris"): string {
	return quand.toLocaleDateString("fr-CA", { timeZone: fuseau });
}

/**
 * La fiche d'entreprise, en DEUX moitiés qui n'ont pas le même statut.
 *
 * Au-dessus, l'administratif : il vient de l'annuaire de l'État, il ne se saisit
 * pas, et il ne se corrige pas ici. Ce sont les clés de dédoublonnage — deux index
 * uniques partiels reposent sur `siren` et `siret`.
 *
 * En dessous, l'éditorial : personne ne peut le déduire, donc l'entreprise l'écrit.
 * C'est la seule moitié que `PATCH /api/espace/entreprise` accepte.
 */
export type EspaceCompany = {
	name: string;
	siren: string | null;
	siret: string | null;
	address: string | null;
	postalCode: string | null;
	city: string | null;

	tagline: string | null;
	description: string | null;
	website: string | null;
	sector: string | null;
	size: string | null;
	foundedYear: number | null;
	updatedAt: string | null;
};

/** Les listes fermées, reprises du proto école pour que les fiches se comparent. */
export const COMPANY_SECTORS = [
	"Agroalimentaire",
	"Banque, assurance",
	"BTP, construction",
	"Commerce, distribution",
	"Conseil, audit",
	"Éducation, formation",
	"Énergie, environnement",
	"Industrie",
	"Informatique, numérique",
	"Média, communication",
	"Santé, social",
	"Services aux entreprises",
	"Transport, logistique",
	"Autre",
] as const;

export const COMPANY_SIZES = [
	"1 à 9 salariés",
	"10 à 49 salariés",
	"50 à 249 salariés",
	"250 à 999 salariés",
	"1 000 salariés et plus",
] as const;

export const TAGLINE_MAX = 70;
export const DESCRIPTION_MAX = 1500;

export type EspaceSchool = {
	slug: string;
	name: string;
	logoUrl: string | null;
	/** La couleur d'action, en HSL sans la fonction : c'est elle qui teinte la coque. */
	accent: string | null;
	accentContrast: string | null;
	/** L'aplat de navigation, quand l'école en veut un autre que sa couleur d'action. */
	navBase: string | null;
};

export type EspaceOverview = {
	contact: { fullName: string | null; email: string; phone: string | null };
	company: EspaceCompany | null;
	schools: EspaceSchool[];
	requests: EspaceRequest[];
	selections: EspaceSelection[];
};
